import { useState, useEffect } from 'react'
import {
  Card, Table, Tag, Button, Statistic, Row, Col, Tabs, Empty, Spin,
  message, Modal, Form, Input, Select, Typography, Progress, Alert, Badge
} from 'antd'
import {
  ReloadOutlined, PlusOutlined, SearchOutlined, RobotOutlined,
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, LinkOutlined,
  RadarChartOutlined, SettingOutlined, ThunderboltOutlined
} from '@ant-design/icons'
import { Line, Column } from '@ant-design/charts'
import { aiVisibilityAPI } from '../services/api'

const { Title, Text } = Typography
const { TabPane } = Tabs

const AIVisibility = () => {
  const [loading, setLoading] = useState(false)
  const [checkingSearch, setCheckingSearch] = useState(false)
  const [checkingCrawlers, setCheckingCrawlers] = useState(false)
  const [summary, setSummary] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [searchTrends, setSearchTrends] = useState([])
  const [crawlerActivity, setCrawlerActivity] = useState([])
  const [crawlerSummary, setCrawlerSummary] = useState([])
  const [queries, setQueries] = useState([])
  const [configStatus, setConfigStatus] = useState(null)
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [summaryData, searchData, trendsData, crawlerData, crawlerSum, queriesData, configData] = await Promise.all([
        aiVisibilityAPI.getSummary().catch(() => null),
        aiVisibilityAPI.getSearchResults().catch(() => []),
        aiVisibilityAPI.getSearchTrends().catch(() => []),
        aiVisibilityAPI.getCrawlerActivity().catch(() => []),
        aiVisibilityAPI.getCrawlerSummary().catch(() => []),
        aiVisibilityAPI.getQueries().catch(() => []),
        aiVisibilityAPI.getConfigStatus().catch(() => null)
      ])

      setSummary(summaryData)
      setSearchResults(searchData || [])
      setSearchTrends(trendsData || [])
      setCrawlerActivity(crawlerData || [])
      setCrawlerSummary(crawlerSum || [])
      setQueries(queriesData || [])
      setConfigStatus(configData)
    } catch (err) {
      message.error('Failed to load AI visibility data')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckSearch = async (allQueries = false) => {
    setCheckingSearch(true)
    try {
      await aiVisibilityAPI.triggerSearchCheck(allQueries)
      message.success(`AI search check started (${allQueries ? 'all queries' : 'top 3'}). Refresh in a few minutes.`)
    } catch (err) {
      message.error('Failed to start search check')
    } finally {
      setCheckingSearch(false)
    }
  }

  const handleCheckCrawlers = async () => {
    setCheckingCrawlers(true)
    try {
      await aiVisibilityAPI.triggerCrawlerCheck()
      message.success('Crawler activity check started.')
      setTimeout(fetchData, 3000)
    } catch (err) {
      message.error('Failed to check crawler activity')
    } finally {
      setCheckingCrawlers(false)
    }
  }

  const handleAddQuery = async (values) => {
    try {
      await aiVisibilityAPI.addQuery(values)
      message.success('Query added')
      setAddModalVisible(false)
      form.resetFields()
      fetchData()
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to add query')
    }
  }

  const handleDeleteQuery = async (id) => {
    try {
      await aiVisibilityAPI.deleteQuery(id)
      message.success('Query removed')
      fetchData()
    } catch (err) {
      message.error('Failed to remove query')
    }
  }

  const searchResultsColumns = [
    {
      title: 'Query',
      dataIndex: 'query',
      key: 'query',
      render: (text) => <Text strong style={{ fontSize: 12 }}>{text}</Text>
    },
    {
      title: 'Engine',
      dataIndex: 'engine_name',
      key: 'engine',
      width: 100,
      render: (name) => <Tag color="blue">{name}</Tag>
    },
    {
      title: 'Mentioned',
      dataIndex: 'mentioned',
      key: 'mentioned',
      width: 100,
      render: (mentioned) => mentioned ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
      ) : (
        <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
      )
    },
    {
      title: 'Cited',
      dataIndex: 'cited',
      key: 'cited',
      width: 80,
      render: (cited) => cited ? (
        <Tag color="green" icon={<LinkOutlined />}>Yes</Tag>
      ) : (
        <Tag color="default">No</Tag>
      )
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 80,
      render: (pos) => pos ? <Tag color="gold">#{pos}</Tag> : '-'
    },
    {
      title: 'Date',
      dataIndex: 'search_date',
      key: 'date',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString()
    }
  ]

  const crawlerColumns = [
    {
      title: 'Bot',
      dataIndex: 'bot_name',
      key: 'bot',
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{record.bot_description}</Text>
        </div>
      )
    },
    {
      title: 'Requests',
      dataIndex: 'total_requests',
      key: 'requests',
      width: 100,
      sorter: (a, b) => a.total_requests - b.total_requests,
      render: (count) => <Tag color={count > 100 ? 'green' : count > 10 ? 'blue' : 'default'}>{count}</Tag>
    },
    {
      title: 'Days Active',
      dataIndex: 'days_active',
      key: 'days',
      width: 100,
      render: (days) => `${days} days`
    },
    {
      title: 'Last Visit',
      dataIndex: 'last_visit',
      key: 'last',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString()
    }
  ]

  const queriesColumns = [
    {
      title: 'Query',
      dataIndex: 'query',
      key: 'query'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cat) => <Tag>{cat}</Tag>
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (p) => <Tag color={p >= 8 ? 'red' : p >= 5 ? 'orange' : 'default'}>{p}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      width: 80,
      render: (active) => <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button size="small" danger onClick={() => handleDeleteQuery(record.id)}>
          Remove
        </Button>
      )
    }
  ]

  // Trends chart config
  const trendsChartConfig = {
    data: searchTrends,
    xField: 'date',
    yField: 'mention_rate',
    height: 250,
    yAxis: {
      title: { text: 'Mention Rate (%)' },
      min: 0,
      max: 100
    },
    point: { size: 4 },
    color: '#52c41a',
    tooltip: {
      formatter: (datum) => ({
        name: 'Mention Rate',
        value: `${datum.mention_rate}%`
      })
    }
  }

  // Crawler chart config
  const crawlerChartConfig = {
    data: crawlerSummary.slice(0, 10),
    xField: 'bot_name',
    yField: 'total_requests',
    height: 250,
    color: '#1890ff',
    label: {
      position: 'top',
      style: { fill: '#666' }
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <RobotOutlined style={{ marginRight: 8 }} />
            AI Visibility
          </Title>
          {summary?.last_search_check && (
            <Text type="secondary">
              Last check: {new Date(summary.last_search_check).toLocaleString()}
            </Text>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            icon={<RadarChartOutlined />}
            onClick={handleCheckCrawlers}
            loading={checkingCrawlers}
          >
            Check Crawlers
          </Button>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => handleCheckSearch(false)}
            loading={checkingSearch}
          >
            Check AI Search
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* Config Alert */}
          {configStatus && !configStatus.perplexity_api?.configured && (
            <Alert
              type="warning"
              message="Perplexity API Not Configured"
              description={
                <span>
                  Add your Perplexity API key to AWS Secrets Manager at <code>prod/acis-ai/perplexity</code> to enable automated AI search checking.
                  Get a key at <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer">perplexity.ai/settings/api</a>
                </span>
              }
              style={{ marginBottom: 24 }}
              showIcon
            />
          )}

          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="Queries Tracked"
                  value={summary?.total_queries || 0}
                  prefix={<SearchOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="Mentioned"
                  value={summary?.queries_mentioned || 0}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<EyeOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="Mention Rate"
                  value={summary?.mention_rate || 0}
                  suffix="%"
                  valueStyle={{ color: summary?.mention_rate > 20 ? '#52c41a' : '#faad14' }}
                />
                <Progress
                  percent={summary?.mention_rate || 0}
                  strokeColor={summary?.mention_rate > 20 ? '#52c41a' : '#faad14'}
                  showInfo={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="Cited"
                  value={summary?.queries_cited || 0}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<LinkOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="AI Crawlers"
                  value={summary?.total_crawlers || 0}
                  prefix={<RobotOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Card>
                <Statistic
                  title="Crawler Requests"
                  value={summary?.total_crawler_requests || 0}
                  prefix={<ThunderboltOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Card>
            <Tabs defaultActiveKey="search">
              <TabPane tab="AI Search Results" key="search">
                {searchResults.length > 0 ? (
                  <Table
                    columns={searchResultsColumns}
                    dataSource={searchResults}
                    rowKey={(r) => `${r.search_date}-${r.query}-${r.engine_name}`}
                    pagination={{ pageSize: 20 }}
                    size="small"
                  />
                ) : (
                  <Empty description="No search results yet. Click 'Check AI Search' to test." />
                )}
              </TabPane>

              <TabPane tab="Trends" key="trends">
                {searchTrends.length > 0 ? (
                  <div>
                    <Title level={5}>Mention Rate Over Time</Title>
                    <Line {...trendsChartConfig} />
                  </div>
                ) : (
                  <Empty description="No trend data yet. Run AI search checks over multiple days." />
                )}
              </TabPane>

              <TabPane tab="AI Crawlers" key="crawlers">
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Title level={5}>Crawler Activity by Bot</Title>
                    {crawlerSummary.length > 0 ? (
                      <Table
                        columns={crawlerColumns}
                        dataSource={crawlerSummary}
                        rowKey="bot_name"
                        pagination={false}
                        size="small"
                      />
                    ) : (
                      <Empty description="No AI crawler activity detected yet" />
                    )}
                  </Col>
                  <Col xs={24} lg={12}>
                    <Title level={5}>Requests by Bot</Title>
                    {crawlerSummary.length > 0 ? (
                      <Column {...crawlerChartConfig} />
                    ) : (
                      <Empty description="No data for chart" />
                    )}
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Test Queries" key="queries">
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddModalVisible(true)}
                  >
                    Add Query
                  </Button>
                  <Button
                    style={{ marginLeft: 8 }}
                    onClick={() => handleCheckSearch(true)}
                    loading={checkingSearch}
                  >
                    Test All Queries
                  </Button>
                </div>
                <Table
                  columns={queriesColumns}
                  dataSource={queries}
                  rowKey="id"
                  pagination={{ pageSize: 15 }}
                  size="small"
                />
              </TabPane>

              <TabPane tab="Configuration" key="config">
                {configStatus ? (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Card title="llms.txt" size="small">
                        <p>
                          <Badge status={configStatus.llms_txt?.exists ? 'success' : 'error'} />
                          {configStatus.llms_txt?.exists ? 'Configured' : 'Missing'}
                        </p>
                        {configStatus.llms_txt?.exists && (
                          <p><Text type="secondary">{configStatus.llms_txt.lines} lines</Text></p>
                        )}
                        <a href={configStatus.llms_txt?.url} target="_blank" rel="noopener noreferrer">
                          View llms.txt
                        </a>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card title="robots.txt AI Rules" size="small">
                        <p>
                          <Badge status={configStatus.robots_txt?.ai_bots_configured > 0 ? 'success' : 'warning'} />
                          {configStatus.robots_txt?.ai_bots_configured || 0} AI bots configured
                        </p>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card title="Perplexity API" size="small">
                        <p>
                          <Badge status={configStatus.perplexity_api?.configured ? 'success' : 'error'} />
                          {configStatus.perplexity_api?.configured ? 'Configured' : 'Not configured'}
                        </p>
                        {!configStatus.perplexity_api?.configured && (
                          <p><Text type="secondary">Add key to: {configStatus.perplexity_api?.secret_path}</Text></p>
                        )}
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <Empty description="Configuration status unavailable" />
                )}
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}

      {/* Add Query Modal */}
      <Modal
        title="Add Test Query"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddQuery}>
          <Form.Item
            name="query"
            label="Query"
            rules={[{ required: true, message: 'Please enter a query' }]}
          >
            <Input placeholder="e.g., best AI stock picking software 2025" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            initialValue="general"
          >
            <Select>
              <Select.Option value="high_intent">High Intent</Select.Option>
              <Select.Option value="product">Product</Select.Option>
              <Select.Option value="comparison">Comparison</Select.Option>
              <Select.Option value="strategy">Strategy</Select.Option>
              <Select.Option value="long_tail">Long Tail</Select.Option>
              <Select.Option value="general">General</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority (1-10)"
            initialValue={5}
          >
            <Select>
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(p => (
                <Select.Option key={p} value={p}>{p} {p >= 8 ? '(High)' : p >= 5 ? '(Medium)' : '(Low)'}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Add Query
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AIVisibility
